/**
 * The dependency graph, as plain functions over keys and edges.
 *
 * Carried over from illgati's store, where the same three questions --
 * "would this edge close a loop?", "what order do these go in?", "is B
 * downstream of A?" -- are answered once and asked by everything else. The
 * template editor asks before drawing an arrow, validation asks before saving,
 * and the instance engine asks before unblocking; they cannot disagree because
 * there is only one answer.
 */

export interface Edge {
  from: string;
  to: string;
}

function successors(edges: readonly Edge[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const { from, to } of edges) {
    const list = out.get(from);
    if (list) list.push(to);
    else out.set(from, [to]);
  }
  return out;
}

/** True when `to` can be reached from `from` by following edges forward. A node reaches itself. */
export function reaches(from: string, to: string, edges: readonly Edge[]): boolean {
  if (from === to) return true;
  const next = successors(edges);
  const seen = new Set<string>([from]);
  const stack = [from];
  while (stack.length > 0) {
    const at = stack.pop()!;
    for (const step of next.get(at) ?? []) {
      if (step === to) return true;
      if (!seen.has(step)) {
        seen.add(step);
        stack.push(step);
      }
    }
  }
  return false;
}

/** Would adding `from -> to` close a loop? The question the editor asks before drawing an arrow. */
export function wouldCycle(from: string, to: string, edges: readonly Edge[]): boolean {
  return reaches(to, from, edges);
}

/**
 * One cycle, as the keys around it with the first repeated at the end
 * (`["a", "b", "a"]`), or null when the graph is acyclic. Returning the loop
 * rather than a boolean is what lets validation name the tasks involved.
 */
export function findCycle(keys: readonly string[], edges: readonly Edge[]): string[] | null {
  const next = successors(edges);
  const state = new Map<string, "open" | "done">();
  const path: string[] = [];

  const visit = (key: string): string[] | null => {
    state.set(key, "open");
    path.push(key);
    for (const step of next.get(key) ?? []) {
      const seen = state.get(step);
      if (seen === "open") return [...path.slice(path.indexOf(step)), step];
      if (seen === undefined) {
        const found = visit(step);
        if (found) return found;
      }
    }
    path.pop();
    state.set(key, "done");
    return null;
  };

  for (const key of keys) {
    if (state.has(key)) continue;
    const found = visit(key);
    if (found) return found;
  }
  return null;
}

/**
 * Keys in an order where every edge points forward, ties broken by the order
 * the keys were given in -- so a template whose nodes are already listed in a
 * sensible order comes back in that order. Throws on a cycle: callers validate
 * first, and a cycle reaching here is a bug.
 */
export function topologicalOrder(keys: readonly string[], edges: readonly Edge[]): string[] {
  const position = new Map(keys.map((key, index) => [key, index]));
  const incoming = new Map<string, number>(keys.map((key) => [key, 0]));
  for (const { to } of edges) incoming.set(to, (incoming.get(to) ?? 0) + 1);
  const next = successors(edges);

  const ready = keys.filter((key) => incoming.get(key) === 0);
  const out: string[] = [];
  while (ready.length > 0) {
    ready.sort((a, b) => position.get(a)! - position.get(b)!);
    const key = ready.shift()!;
    out.push(key);
    for (const step of next.get(key) ?? []) {
      const left = incoming.get(step)! - 1;
      incoming.set(step, left);
      if (left === 0) ready.push(step);
    }
  }
  if (out.length !== keys.length) throw new Error("topologicalOrder was handed a cycle.");
  return out;
}
